import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postHelpForm } from '../api/postHelpForm';
import { ConsentCheckBox } from '../components/consentCheckbox/ConsentCheckBox';
import { Footer } from '../components/Footer/Footer';
import { FormStyled } from '../components/form/Form.styled';
import { InputTexFieldForm } from '../components/form/InputTextField';
import { Messageform } from '../components/form/MessageForm';
import { TextAreaFieldForm } from '../components/form/TextAreaField';
import { Header } from '../components/Header/Header';
import { Tooltip } from '../components/Tooltip/Tooltip';
import { routes } from '../constants/constants';
import { extractUrlParam } from '../helper/extractUrlParam';
import { useLocation } from '../react-router-hooks';
import { theme } from '../theme';

export const HelpFormScreen = () => {
  const { t } = useTranslation();
  const { location } = useLocation();
  const [paramsState, setParamsState] = useState<any>();
  const [disableButton, setDisableButton] = useState(false);
  const [formState, setFormState] = useState('');
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!location.state || !location.state.uuid) {
      const url = location.search;
      setParamsState({
        uuid: extractUrlParam({ url, type: 'uuid' }),
        model: extractUrlParam({ url, type: 'model' }),
        appVersion: extractUrlParam({ url, type: 'appVersion' }),
        systemVersion: extractUrlParam({ url, type: 'systemVersion' }),
        customer: extractUrlParam({ url, type: 'customer' })
      });
    } else {
      setParamsState(location.state);
    }
  }, [location]);

  return (
    <div className="root">
      <Header
        isFormHelp={true}
        router={{ pathname: routes.FEEDBACKFORM, state: paramsState }}
      />
      <div id="main" className="main">
        <FormStyled.ContainerMain>
          <FormStyled.Container>
            <Messageform
              title="Tell us what went wrong"
              message="Thank you for contacting Nect. We’re sorry that things didn’t work out for you. Please let us know what the problem was and our support team will be on their way."
              link="Browse frequently asked questions"
              to={routes.FAQMAIN}
            />
            <div
              style={{
                marginTop: 24,
                marginBottom: 24
              }}
            >
              <Formik
                initialValues={{
                  problem: '',
                  name: '',
                  email: '',
                  caseid: (paramsState && paramsState.uuid) || '',
                  company: '',
                  checkBox: ''
                }}
                enableReinitialize={true}
                validate={values => {
                  const errors: any = {};
                  const email: string = values.email.trim();
                  const caseid: string = values.caseid.trim();
                  if (!values.company) {
                    errors.company = t('This field is required.');
                  } else {
                    setDisableButton(false);
                  }
                  if (!values.problem) {
                    errors.problem = t('This field is required.');
                  } else {
                    setDisableButton(false);
                    if (values.problem.length < 15) {
                      errors.problem = t(
                        'Please enter at least 15 characters.'
                      );
                    }
                  }
                  if (
                    !paramsState?.uuid &&
                    caseid &&
                    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
                      caseid
                    ) &&
                    !/^[0-9a-f]{8}$/i.test(caseid)
                  ) {
                    errors.caseid = t('This is not a valid Case ID.');
                  }
                  if (!email) {
                    errors.email = t('This field is required.');
                  } else {
                    setDisableButton(false);
                    if (
                      !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/i.test(
                        email
                      )
                    ) {
                      errors.email = t(
                        'Your email address should contain an ‘@’.'
                      );
                    }
                  }
                  if (!checked) {
                    errors.checkBox = t(
                      'Please check the box below before you submit'
                    );
                  }
                  return errors;
                }}
                onSubmit={async (values, { resetForm }) => {
                  setDisableButton(true);
                  const response = await postHelpForm({
                    state: paramsState,
                    values: {
                      ...values,
                      uuid: values.caseid ?? paramsState?.uuid
                    }
                  });
                  setDisableButton(false);
                  if (response && response.ok) {
                    resetForm();
                    setFormState('ok');
                  } else {
                    setFormState('error');
                  }
                }}
              >
                {({ values, errors, touched, handleChange, handleSubmit }) => (
                  <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                    <InputTexFieldForm
                      name="name"
                      question="Your full name"
                      hint="Max Mustermann"
                      value={values.name}
                      onChange={handleChange}
                    />
                    <InputTexFieldForm
                      name="email"
                      value={values.email}
                      question="Your email address <b>*required</b>"
                      hint="max@mustermann.com"
                      onChange={handleChange}
                      color={
                        errors.email && touched.email ? theme.red : theme.gray
                      }
                    />
                    <FormStyled.ValidationMessage>
                      {errors.email && touched.email && errors.email}
                    </FormStyled.ValidationMessage>

                    {!paramsState?.uuid && (
                      <>
                        <InputTexFieldForm
                          name="caseid"
                          value={values.caseid}
                          question="Case ID"
                          hint="abef1234"
                          onChange={handleChange}
                          color={
                            errors.caseid && touched.caseid
                              ? theme.red
                              : theme.gray
                          }
                          tooltip={
                            <Tooltip
                              title={t('Case ID')}
                              text={t(
                                'The Case ID is a eight-digit sequence of numbers and letters which you can find here within the Nect Wallet:'
                              )}
                              image={require(`../assets/case-id-tooltip-${t(
                                'langCode'
                              )}.jpg`)}
                            />
                          }
                        />
                        <FormStyled.ValidationMessage>
                          {errors.caseid && touched.caseid && errors.caseid}
                        </FormStyled.ValidationMessage>
                      </>
                    )}

                    <TextAreaFieldForm
                      name="problem"
                      question="Describe the problem in as much detail as you can <b>*required</b>"
                      hint="Please be specific"
                      value={values.problem}
                      onChange={handleChange}
                      color={
                        errors.problem && touched.problem
                          ? theme.red
                          : theme.gray
                      }
                    />
                    <FormStyled.ValidationMessage>
                      {errors.problem && touched.problem && errors.problem}
                    </FormStyled.ValidationMessage>
                    <InputTexFieldForm
                      name="company"
                      value={values.company}
                      question="Which company were you identifying for? <b>*required</b>"
                      hint="Pflegeversicherung"
                      onChange={handleChange}
                      color={
                        errors.company && touched.company
                          ? theme.red
                          : theme.gray
                      }
                    />
                    <FormStyled.ValidationMessage>
                      {errors.company && touched.company && errors.company}
                    </FormStyled.ValidationMessage>
                    <FormStyled.ButtonSubmit
                      type="submit"
                      disabled={disableButton}
                      style={{
                        color: disableButton ? theme.gray : theme.primary,
                        background: disableButton
                          ? theme.grayLight
                          : theme.primaryLight,
                        borderColor: disableButton
                          ? theme.grayLight
                          : theme.primaryLight
                      }}
                    >
                      {disableButton ? t('Submitted') : t('Submit')}
                    </FormStyled.ButtonSubmit>
                    {formState === 'ok' && (
                      <FormStyled.SentMessage>
                        {t(
                          'Thank you for contacting us. Our support team will be with you soon.'
                        )}
                      </FormStyled.SentMessage>
                    )}
                    {formState === 'error' && (
                      <FormStyled.SentMessage
                        style={{ color: theme.red }}
                        dangerouslySetInnerHTML={{
                          __html: t(
                            "An unknown error has occurred. Please contact our support team at <a href='mailto:service@nect.com'>service@nect.com</a>."
                          )
                        }}
                      />
                    )}
                    <FormStyled.ValidationMessage>
                      {errors.checkBox && touched.checkBox && errors.checkBox}
                    </FormStyled.ValidationMessage>
                    <FormStyled.BottomSpan>
                      <ConsentCheckBox
                        onChange={() => {
                          setChecked(!checked);
                        }}
                        checked={checked}
                        disabled={false}
                        text={t(
                          "I have read and understood Nect's Data Protection Policy. Your data provided here will only be processed for other purposes if you have given separate consent or if a legal regulation permits this processing."
                        )}
                      />
                    </FormStyled.BottomSpan>
                  </form>
                )}
              </Formik>
            </div>
          </FormStyled.Container>
        </FormStyled.ContainerMain>
      </div>
      <Footer />
    </div>
  );
};
